import { LOGBOOK_CLASSES } from '@/features/aircraft/classes';
import type {
  LogbookApproaches,
  LogbookClass,
  LogbookEntry,
  LogbookLandings,
  LogbookTakeoffs,
  LogbookTimeMatrix,
} from '@/lib/db';

import { formatDecimalHours, parseDurationHours, roundDecimalHours } from './time';

/**
 * One column of the logbook grid, and the two operations everything else in
 * this feature is built from: read a cell as the text it should show, and
 * write typed text back into an entry.
 *
 * A single list drives the header row, the cell renderer, keyboard
 * navigation and fill-down — so a column that exists is a column that works
 * everywhere, and adding one is one entry here rather than four places kept
 * in sync by hand.
 */

/**
 * The banks of columns CAA Form 1373 prints, in the order it prints them.
 *
 * Shared between the editable grid and the derived 1373 table in
 * `caa-columns.ts`, so the two read as the same logbook seen twice rather
 * than two tables that happen to be nearby.
 */
export type ColumnGroup =
  | 'flight'
  | 'total'
  | 'time'
  | 'single-engine'
  | 'multi-engine'
  | 'instrument'
  | 'other'
  | 'takeoffs'
  | 'landings';

/**
 * What a cell's `<input>` will accept while a pilot is still typing.
 *
 * `write` below is the gate on a *finished* value; this is the narrower one
 * on every keystroke, so a date cell never holds a letter and an aerodrome
 * cell never holds a fifth character even for a moment. The two are separate
 * on purpose: fill-down, the API-match flow and interchange all produce
 * values nobody typed, and those still have to pass `write`.
 */
export interface CellInput {
  /** Which on-screen keyboard a phone should offer. */
  inputMode?: 'numeric' | 'decimal' | 'text';
  maxLength?: number;
  placeholder?: string;
  /**
   * Rewrites the text mid-edit: drops characters that can never appear in
   * this column, normalises the ones that can, and caps the length. Runs on
   * every keystroke including the first, so it has to accept half-typed
   * input and be idempotent — `sanitise(sanitise(x)) === sanitise(x)`.
   */
  sanitise: (raw: string) => string;
}

export interface LogbookColumn {
  id: string;
  label: string;
  /** Shown once, above the columns it covers, the way a paper logbook page groups them. */
  group: ColumnGroup;
  groupLabel: string;
  /**
   * A second header row between the group and the column — 1373's DAY and
   * NIGHT bands inside SINGLE ENGINE and MULTI ENGINE. Absent on a column
   * whose group has no middle band, which is most of them.
   */
  subGroupLabel?: string;
  align: 'left' | 'right';
  width: string;
  /** Typing rules for this cell. Absent means "any text"; `write` still decides what may be stored. */
  input?: CellInput;
  /**
   * True for a column that only ever displays a computed value — the
   * per-class hour columns below, which show this entry's total under
   * whichever `LogbookClass` it's flown as. Never enters edit mode; `write`
   * is a no-op kept only so every column still round-trips through the same
   * interface.
   */
  readOnly?: boolean;
  /** The text a cell shows, in and out of edit mode. */
  read: (entry: LogbookEntry) => string;
  /** Applies typed text to an entry, returning a new entry — or `undefined` if the text cannot be parsed. */
  write: (entry: LogbookEntry, raw: string) => LogbookEntry | undefined;
  /**
   * This column summed over a set of entries, as the text a footer cell
   * shows. Absent on a column there is nothing sensible to add up — a date,
   * a registration, a name.
   *
   * On the column rather than in a totals module because a column that
   * exists is a column that works everywhere, including in a footer: the
   * page totals, the running totals and the career totals a 1373 page
   * carries are all this same fold over a different set of rows, and a new
   * hour column should not need a second edit somewhere else to be counted.
   */
  total?: (entries: readonly LogbookEntry[]) => string;
}

/*
 * ---------------------------------------------------------------------------
 * Typing rules
 *
 * Each of these builds one `CellInput.sanitise`. They are deliberately dumb
 * character filters rather than validators: a sanitiser runs on half-typed
 * text, so it can only ever say "that character cannot appear here", never
 * "that value is wrong". Saying the second is `write`'s job, further down.
 * ---------------------------------------------------------------------------
 */

/**
 * Drops every character the pattern matches, keeping the rest in order.
 *
 * Each pattern below is written negated and global — `/[^0-9]/g` rather than
 * a per-character test — so this is one `replace` over the string rather than
 * a walk over it, and no code point is ever split into surrogate halves on
 * the way.
 */
function stripChars(disallowed: RegExp): (raw: string) => string {
  return (raw) => raw.replace(disallowed, '');
}

const NOT_DIGIT = /[^0-9]/g;
/** Anything outside the alphabet `parseDurationHours` understands: `1:30`, `1.5`, `90m`, `2h`. */
const NOT_DURATION_CHAR = /[^0-9:.himns ]/gi;
const NOT_CODE_CHAR = /[^A-Z0-9]/g;
/** Aerodrome codes plus the separators a multi-leg Intermediate cell needs. */
const NOT_INTERMEDIATE_CHAR = /[^A-Z0-9 ,/]/g;
const NOT_REGISTRATION_CHAR = /[^A-Z0-9-]/g;

/** ICAO is four; NZ's unlisted strips get a four-character code of the pilot's own invention rather than a sentence. */
const AERODROME_CODE_LENGTH = 4;
const AERODROME_CODE = /^[A-Z0-9]{1,4}$/;

/**
 * `20260314`, `2026-3-14` and `2026-03-14` all land on `2026-03-14`, with the
 * dashes put in as the pilot types rather than typed. Anything that isn't a
 * digit never reaches the cell at all, so the only way to leave a date cell
 * wrong is to leave it half-finished — which `write` then refuses.
 */
export function sanitiseDateText(raw: string): string {
  const digits = stripChars(NOT_DIGIT)(raw).slice(0, 8);
  return [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)]
    .filter((part) => part !== '')
    .join('-');
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True only for a day that actually happened.
 *
 * The shape test alone would let `2026-02-30` through, and a logbook holding
 * dates that never existed is worse than a rejected keystroke: every total,
 * every currency window and every printed page is a fold over these.
 */
export function isCalendarDate(value: string): boolean {
  const match = ISO_DATE.exec(value);
  if (match === null) {
    return false;
  }
  const [, yearText = '', monthText = '', dayText = ''] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const utc = new Date(Date.UTC(year, month - 1, day));
  // Round-tripping catches the overflow `Date` does silently — 30 February
  // becomes 2 March, and 2 March is not the day that was typed.
  return (
    utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === day
  );
}

const sanitiseDuration = (raw: string): string =>
  stripChars(NOT_DURATION_CHAR)(raw).slice(0, 8).toLowerCase();
const sanitiseCount = (raw: string): string => stripChars(NOT_DIGIT)(raw).slice(0, 3);
const sanitiseAerodrome = (raw: string): string =>
  stripChars(NOT_CODE_CHAR)(raw.toUpperCase()).slice(0, AERODROME_CODE_LENGTH);
const sanitiseIntermediate = (raw: string): string =>
  stripChars(NOT_INTERMEDIATE_CHAR)(raw.toUpperCase()).slice(0, INTERMEDIATE_MAX_LENGTH);
const sanitiseRegistration = (raw: string): string =>
  stripChars(NOT_REGISTRATION_CHAR)(raw.toUpperCase()).slice(0, 10);

const DURATION_INPUT: CellInput = {
  maxLength: 8,
  placeholder: '1.5',
  sanitise: sanitiseDuration,
};
const COUNT_INPUT: CellInput = { inputMode: 'numeric', maxLength: 3, sanitise: sanitiseCount };
const AERODROME_INPUT: CellInput = {
  maxLength: AERODROME_CODE_LENGTH,
  placeholder: 'ICAO',
  sanitise: sanitiseAerodrome,
};

/**
 * The Intermediate cell is a list of aerodromes typed as one string.
 *
 * 1373 prints INTERMEDIATE as a single column but it holds an ordered list,
 * and `LogbookEntry.intermediate` stores it as one — see that field's own
 * doc comment for why the legs have to stay apart. These two functions are
 * the only place the list becomes a cell and the cell becomes a list again,
 * and they round-trip: `parseIntermediate(formatIntermediate(legs))` is
 * `legs` for any list of codes.
 */
const INTERMEDIATE_MAX_LENGTH = 34;

/** `['NZNS', 'NZWP']` → `'NZNS, NZWP'` — how a paper logbook writes a two-stop day. */
export function formatIntermediate(legs: readonly string[] | undefined): string {
  return legs === undefined ? '' : legs.join(', ');
}

/**
 * `'NZNS NZWP'`, `'NZNS, NZWP'` or `'NZNS/NZWP'` → `['NZNS', 'NZWP']`.
 *
 * All three separators, because all three are how pilots write a route and
 * the old single-string Via cell accepted all three — a converted entry has
 * to come back out the way it went in.
 *
 * @returns the legs in order, or `undefined` if any of them is not an
 *          aerodrome code. `undefined` is what makes the cell refuse the
 *          value rather than store half a route.
 */
export function parseIntermediate(value: string): string[] | undefined {
  const legs = value
    .toUpperCase()
    .split(/[\s,/]+/)
    .filter((leg) => leg !== '');
  return legs.every((leg) => AERODROME_CODE.test(leg)) ? legs : undefined;
}

type OptionalStringKey = 'registration' | 'aircraftType' | 'from' | 'to' | 'otherCrew' | 'remarks';

interface TextColumnOptions {
  id: string;
  label: string;
  group: ColumnGroup;
  groupLabel: string;
  key: OptionalStringKey;
  width?: string;
  /** Normalises a finished value on the way in — uppercasing, mostly. */
  transform?: (value: string) => string;
  /**
   * Refuses a finished value outright, so `write` returns `undefined` and the
   * grid flashes the cell red. Unlike `input.sanitise` this also guards the
   * paths nobody typed into — fill-down, and the API-match flow.
   */
  validate?: (value: string) => boolean;
  input?: CellInput;
}

/**
 * A free-text field that is genuinely optional on `LogbookEntry` — the schema
 * has `exactOptionalPropertyTypes` on, so an empty cell has to *omit* the key
 * rather than set it to `undefined`.
 */
function optionalTextColumn(options: TextColumnOptions): LogbookColumn {
  const { id, label, group, groupLabel, key, width = 'w-28', transform, validate } = options;
  return {
    id,
    label,
    group,
    groupLabel,
    align: 'left',
    width,
    ...(options.input === undefined ? {} : { input: options.input }),
    read: (entry) => entry[key] ?? '',
    write: (entry, raw) => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        // Rest-destructure the key away rather than `delete` it: the schema
        // has `exactOptionalPropertyTypes` on, so an empty cell has to omit
        // the field entirely, and a computed `delete entry[key]` is the one
        // dynamic-property pattern the lint config refuses on principle.
        const { [key]: _omitted, ...rest } = entry;
        return rest;
      }
      const value = transform ? transform(trimmed) : trimmed;
      if (validate && !validate(value)) {
        return undefined;
      }
      return { ...entry, [key]: value };
    },
  };
}

/**
 * The shared folds behind every column's `total`.
 *
 * Hours are rounded once, at the end, rather than per entry: rounding a
 * thousand two-decimal numbers as they are added is how a career total ends
 * up a tenth of an hour out from the column it was added from.
 */
function sumHours(
  entries: readonly LogbookEntry[],
  hours: (entry: LogbookEntry) => number,
): string {
  const total = roundDecimalHours(entries.reduce((sum, entry) => sum + hours(entry), 0));
  return total === 0 ? '' : formatDecimalHours(total);
}

function sumCounts(
  entries: readonly LogbookEntry[],
  count: (entry: LogbookEntry) => number,
): string {
  const total = entries.reduce((sum, entry) => sum + count(entry), 0);
  return total === 0 ? '' : String(total);
}

/** `noUncheckedIndexedAccess` means a keyed read off the time matrix is `number | undefined`. */
function numberOrZero(value: number | LogbookClass): number {
  return typeof value === 'number' ? value : 0;
}

function durationColumn(
  id: string,
  label: string,
  group: ColumnGroup,
  groupLabel: string,
  key: Exclude<keyof LogbookTimeMatrix, 'engineClass'>,
  subGroupLabel?: string,
): LogbookColumn {
  return {
    id,
    label,
    group,
    groupLabel,
    ...(subGroupLabel === undefined ? {} : { subGroupLabel }),
    align: 'right',
    width: 'w-16',
    input: DURATION_INPUT,
    read: (entry) => {
      const value = entry.time[key];
      return typeof value === 'number' && value !== 0 ? formatDecimalHours(value) : '';
    },
    total: (entries) => sumHours(entries, (entry) => numberOrZero(entry.time[key])),
    write: (entry, raw) => {
      const parsed = parseDurationHours(raw);
      if (parsed === null) {
        return undefined;
      }
      return { ...entry, time: { ...entry.time, [key]: parsed } };
    },
  };
}

/** A non-negative integer count cell — blank reads and writes as `0`, the same convention as a paper logbook's empty box. */
function countColumn(
  id: string,
  label: string,
  group: ColumnGroup,
  groupLabel: string,
  read: (entry: LogbookEntry) => number,
  write: (entry: LogbookEntry, value: number) => LogbookEntry,
): LogbookColumn {
  return {
    id,
    label,
    group,
    groupLabel,
    align: 'right',
    width: 'w-12',
    input: COUNT_INPUT,
    read: (entry) => {
      const value = read(entry);
      return value === 0 ? '' : String(value);
    },
    total: (entries) => sumCounts(entries, read),
    write: (entry, raw) => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        return write(entry, 0);
      }
      if (!/^\d+$/.test(trimmed)) {
        return undefined;
      }
      return write(entry, Number(trimmed));
    },
  };
}

function landingsColumn(
  id: string,
  label: string,
  groupLabel: string,
  key: keyof LogbookLandings,
): LogbookColumn {
  return countColumn(
    id,
    label,
    'landings',
    groupLabel,
    (entry) => entry.landings[key],
    (entry, value) => ({ ...entry, landings: { ...entry.landings, [key]: value } }),
  );
}

function takeoffsColumn(
  id: string,
  label: string,
  groupLabel: string,
  key: keyof LogbookTakeoffs,
): LogbookColumn {
  return countColumn(
    id,
    label,
    'takeoffs',
    groupLabel,
    (entry) => entry.takeoffs[key],
    (entry, value) => ({ ...entry, takeoffs: { ...entry.takeoffs, [key]: value } }),
  );
}

/*
 * ---------------------------------------------------------------------------
 * The columns
 *
 * Order and wording follow CAA Form 1373. Two deliberate departures, both
 * because this is the *editable* view of a logbook rather than a printed
 * page — `caa-columns.ts` holds the form's own table, derived and read-only:
 *
 *  - The hour columns here are the facts a pilot enters (how much at night,
 *    how much in each capacity), not 1373's sixteen cross-tabulated cells.
 *    Typing into sixteen cells that are four facts wearing a disguise is how
 *    a logbook ends up disagreeing with itself.
 *  - Class survives as a column of its own. 1373 has no such column — the
 *    form assumes the reader can tell a twin from a Cessna 172 by the type —
 *    but it is what decides whether a row's hours print under SINGLE ENGINE
 *    or MULTI ENGINE, and a derivation the pilot cannot see or correct is a
 *    derivation they cannot trust.
 * ---------------------------------------------------------------------------
 */

const FLIGHT = 'Flight';
const TOTAL = 'Total';
const TIME = 'Flight Time';
const INSTRUMENT = 'Instrument';
const OTHER = 'Other';
const TAKEOFF = 'Take-offs';
const LAND = 'Landings';

/** 1373's DAY/NIGHT band, and the capacity band beside it. */
const DAY_NIGHT = 'Day / Night';
const CAPACITY = 'Capacity';

/**
 * Compact enough for a narrow grid column; `write` also accepts the full id
 * or label, case-insensitively. Exported for `interchange/`, which needs the
 * exact same short forms and lookup on the way in and out of a CSV cell.
 */
export const ENGINE_CLASS_SHORT: Record<LogbookClass, string> = {
  'single-engine-land': 'SE',
  'multi-engine-land': 'ME',
  sea: 'Sea',
  helicopter: 'Heli',
  gyroplane: 'Gyro',
  glider: 'Glider',
  microlight: 'Micro',
  balloon: 'Balloon',
};

export const ENGINE_CLASS_BY_TEXT = new Map<string, LogbookClass>(
  LOGBOOK_CLASSES.flatMap(
    ({ id, label }) =>
      [
        [id, id],
        [label.toLowerCase(), id],
        [ENGINE_CLASS_SHORT[id].toLowerCase(), id],
        // The old short forms, so a CSV written by an earlier build still
        // imports and so a pilot who types what they are used to is not told
        // their own logbook's vocabulary is wrong.
        [id === 'single-engine-land' ? 'sel' : id === 'multi-engine-land' ? 'mel' : id, id],
      ] as [string, LogbookClass][],
  ),
);

/** A read-only cell: it shows a number this row already implies, and there is nothing to type. */
function derivedHoursColumn(
  id: string,
  label: string,
  group: ColumnGroup,
  groupLabel: string,
  hours: (entry: LogbookEntry) => number,
  subGroupLabel?: string,
): LogbookColumn {
  return {
    id,
    label,
    group,
    groupLabel,
    ...(subGroupLabel === undefined ? {} : { subGroupLabel }),
    align: 'right',
    width: 'w-16',
    readOnly: true,
    read: (entry) => {
      const value = hours(entry);
      return value === 0 ? '' : formatDecimalHours(value);
    },
    total: (entries) => sumHours(entries, hours),
    // Computed only. Returning the entry unchanged rather than `undefined`
    // keeps this a harmless no-op instead of an "invalid cell" flash if a
    // pilot tries to type into it anyway.
    write: (entry) => entry,
  };
}

function approachesColumn(id: string, label: string, key: keyof LogbookApproaches): LogbookColumn {
  return countColumn(
    id,
    label,
    'instrument',
    INSTRUMENT,
    (entry) => entry.approaches[key],
    (entry, value) => ({ ...entry, approaches: { ...entry.approaches, [key]: value } }),
  );
}

export const LOGBOOK_COLUMNS: readonly LogbookColumn[] = [
  {
    id: 'date',
    label: 'Date',
    group: 'flight',
    groupLabel: FLIGHT,
    align: 'left',
    width: 'w-24',
    input: {
      inputMode: 'numeric',
      maxLength: 10,
      placeholder: 'YYYY-MM-DD',
      sanitise: sanitiseDateText,
    },
    // `entry.date` is already the local calendar date, `YYYY-MM-DD` — routed
    // through a UTC instant it would round-trip to the wrong day either side
    // of the international date line, which is exactly what `LocalDate`'s own
    // doc comment in `lib/db/types.ts` warns against. So this is the one
    // column that reads and writes the stored string directly.
    read: (entry) => entry.date,
    write: (entry, raw) => {
      const trimmed = raw.trim();
      if (!isCalendarDate(trimmed)) {
        return undefined;
      }
      return { ...entry, date: trimmed };
    },
  },
  /*
   * From, Intermediate and To are three columns, in that order, because that
   * is how 1373 prints a route and how a NZ pilot reads one back. A single
   * "Route" cell would be quicker to build and would lose the one thing the
   * form is asking for: where the aeroplane actually touched down in between.
   *
   * All three hold aerodrome codes, not place names: four characters, upper
   * case, letters and digits. ICAO is four everywhere, and an unlisted NZ
   * strip gets a four-character code of the pilot's own invention — which is
   * what makes a From cell something the currency and totals views can group
   * by. A longer name imported from another logbook is left alone where it
   * sits; it only has to shorten if the pilot edits that cell.
   */
  optionalTextColumn({
    id: 'from',
    label: 'From',
    group: 'flight',
    groupLabel: FLIGHT,
    key: 'from',
    width: 'w-16',
    transform: (value) => value.toUpperCase(),
    validate: (value) => AERODROME_CODE.test(value),
    input: AERODROME_INPUT,
  }),
  {
    id: 'intermediate',
    label: 'Intermediate',
    group: 'flight',
    groupLabel: FLIGHT,
    align: 'left',
    width: 'w-28',
    input: {
      maxLength: INTERMEDIATE_MAX_LENGTH,
      placeholder: 'NZNS, NZWP',
      sanitise: sanitiseIntermediate,
    },
    read: (entry) => formatIntermediate(entry.intermediate),
    write: (entry, raw) => {
      const legs = parseIntermediate(raw.trim());
      if (legs === undefined) {
        return undefined;
      }
      if (legs.length === 0) {
        // Rest-destructure rather than `delete`: `exactOptionalPropertyTypes`
        // means an empty cell has to omit the key, not set it to `undefined`.
        const { intermediate: _omitted, ...rest } = entry;
        return rest;
      }
      return { ...entry, intermediate: legs };
    },
  },
  optionalTextColumn({
    id: 'to',
    label: 'To',
    group: 'flight',
    groupLabel: FLIGHT,
    key: 'to',
    width: 'w-16',
    transform: (value) => value.toUpperCase(),
    validate: (value) => AERODROME_CODE.test(value),
    input: AERODROME_INPUT,
  }),
  optionalTextColumn({
    id: 'aircraftType',
    label: 'Aircraft Type',
    group: 'flight',
    groupLabel: FLIGHT,
    key: 'aircraftType',
    width: 'w-20',
  }),
  optionalTextColumn({
    id: 'registration',
    label: 'Registration',
    group: 'flight',
    groupLabel: FLIGHT,
    key: 'registration',
    transform: (value) => value.toUpperCase(),
    input: { maxLength: 10, placeholder: 'ZK-XXX', sanitise: sanitiseRegistration },
  }),
  {
    id: 'time.engineClass',
    label: 'Class',
    group: 'flight',
    groupLabel: FLIGHT,
    align: 'left',
    width: 'w-16',
    read: (entry) => ENGINE_CLASS_SHORT[entry.time.engineClass],
    write: (entry, raw) => {
      const match = ENGINE_CLASS_BY_TEXT.get(raw.trim().toLowerCase());
      if (match === undefined) {
        return undefined;
      }
      return { ...entry, time: { ...entry.time, engineClass: match } };
    },
  },
  {
    id: 'pic',
    label: 'PIC',
    group: 'flight',
    groupLabel: FLIGHT,
    align: 'left',
    width: 'w-24',
    read: (entry) => entry.pic,
    write: (entry, raw) => ({ ...entry, pic: raw.trim() }),
  },
  optionalTextColumn({
    id: 'otherCrew',
    label: 'Co-Pilot / Student',
    group: 'flight',
    groupLabel: FLIGHT,
    key: 'otherCrew',
    width: 'w-24',
  }),
  optionalTextColumn({
    id: 'remarks',
    label: 'Details of Flight',
    group: 'flight',
    groupLabel: FLIGHT,
    key: 'remarks',
    width: 'w-48',
  }),
  /*
   * Total is derived, and prominent, and never typed — §19 of the redesign
   * brief and the reason the day/night cells sit immediately beside it. A
   * pilot enters 1.5 in Day and the total says 1.5; there is no second place
   * to put the same number and no way for the two to drift.
   */
  derivedHoursColumn('time.totalHr', 'Total', 'total', TOTAL, entryTotalHours),
  durationColumn('time.dayHr', 'Day', 'time', TIME, 'dayHr', DAY_NIGHT),
  durationColumn('time.nightHr', 'Night', 'time', TIME, 'nightHr', DAY_NIGHT),
  durationColumn('time.dualHr', 'Dual', 'time', TIME, 'dualHr', CAPACITY),
  // "PIC", not "P1": 1373 prints PIC, and the identification column above is
  // headed "PIC" for the *name* — which is exactly how the form reads too.
  durationColumn('time.picHr', 'PIC', 'time', TIME, 'picHr', CAPACITY),
  durationColumn('time.coPilotHr', 'Co-Pilot', 'time', TIME, 'coPilotHr', CAPACITY),
  durationColumn(
    'time.commandPracticeHr',
    'Command Practice',
    'time',
    TIME,
    'commandPracticeHr',
    CAPACITY,
  ),
  durationColumn(
    'time.actualInstrumentHr',
    'Actual',
    'instrument',
    INSTRUMENT,
    'actualInstrumentHr',
  ),
  durationColumn(
    'time.simulatedInstrumentHr',
    'Simulated',
    'instrument',
    INSTRUMENT,
    'simulatedInstrumentHr',
  ),
  durationColumn(
    'time.groundInstrumentHr',
    'Ground',
    'instrument',
    INSTRUMENT,
    'groundInstrumentHr',
  ),
  approachesColumn('approaches.nonPrecision', 'Non-Precision', 'nonPrecision'),
  approachesColumn('approaches.precision', 'Precision', 'precision'),
  durationColumn('time.instructorHr', 'Flight Instructor', 'other', OTHER, 'instructorHr'),
  durationColumn(
    'time.ifrCrossCountryHr',
    'IFR Cross Country',
    'other',
    OTHER,
    'ifrCrossCountryHr',
  ),
  takeoffsColumn('takeoffs.day', 'Day', TAKEOFF, 'day'),
  takeoffsColumn('takeoffs.night', 'Night', TAKEOFF, 'night'),
  landingsColumn('landings.day', 'Day', LAND, 'day'),
  landingsColumn('landings.night', 'Night', LAND, 'night'),
  // Not a 1373 column. Touch-and-goes are counted inside the landing columns
  // on the form; this says how many of them were, which is what the
  // takeoff/landing reconciliation in `review.ts` needs and what a pilot
  // counting full-stop landings for currency has to be able to subtract.
  landingsColumn('landings.touchAndGo', 'T&G', LAND, 'touchAndGo'),
];

export function columnById(id: string): LogbookColumn | undefined {
  return LOGBOOK_COLUMNS.find((column) => column.id === id);
}

export interface ColumnGroupRun {
  label: string;
  span: number;
}

/**
 * Consecutive columns collapsed into one spanning header cell.
 *
 * Generic over which label it reads, because a 1373 page has two header
 * bands above the column names — the group (SINGLE ENGINE) and, inside it,
 * the day/night band — and both are the same run-length encoding over the
 * same list. A column with no label at that level ends the run and spans
 * alone, which is what leaves the gap above FROM and TO where the form
 * leaves one.
 */
export function columnGroupRuns(
  columns: readonly LogbookColumn[],
  label: (column: LogbookColumn) => string | undefined,
): ColumnGroupRun[] {
  const runs: ColumnGroupRun[] = [];
  for (const column of columns) {
    const text = label(column) ?? '';
    const last = runs[runs.length - 1];
    if (last?.label === text && text !== '') {
      last.span += 1;
    } else {
      runs.push({ label: text, span: 1 });
    }
  }
  return runs;
}

/**
 * A header's accessible name, qualified only where it has to be.
 *
 * CAA 1373 prints "PIC" twice — once for the pilot's name, once for the
 * capacity hours — and "Day" and "Night" four times over. On paper the
 * grouped bands above them do the disambiguating; a screen reader reading
 * one cell at a time gets none of that, and neither does a test looking for
 * a column by name. So a label that is unique in its table is used as it is,
 * and one that repeats is prefixed with the bands that tell it apart.
 */
export function columnHeaderName(columns: readonly LogbookColumn[], column: LogbookColumn): string {
  const repeated = columns.filter((other) => other.label === column.label).length > 1;
  if (!repeated) {
    return column.label;
  }
  return [column.groupLabel, column.subGroupLabel, column.label]
    .filter((part) => part !== undefined && part !== '')
    .join(' ');
}

/** The top header band of the editable grid. */
export const LOGBOOK_COLUMN_GROUPS: readonly ColumnGroupRun[] = columnGroupRuns(
  LOGBOOK_COLUMNS,
  (column) => column.groupLabel,
);

/** The middle header band: blank above the columns whose group has no sub-band. */
export const LOGBOOK_COLUMN_SUBGROUPS: readonly ColumnGroupRun[] = columnGroupRuns(
  LOGBOOK_COLUMNS,
  (column) => column.subGroupLabel,
);

/** This entry's flight time — day and night are the whole of it, per `LogbookTimeMatrix`'s own contract. */
export function entryTotalHours(entry: LogbookEntry): number {
  return entry.time.dayHr + entry.time.nightHr;
}
